import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, DollarSign, Calendar, Tag, ChevronRight } from 'lucide-react';
import useExpenseStore from '../../store/expenseStore';
import useCategoryStore from '../../store/categoryStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCurrency } from '../../utils/formatters';
import { PRIORITY_LABELS, PRIORITY_COLORS, ROUTES } from '../../utils/constants';

const Analytics = () => {
  const { expenses, getCurrentMonthExpenses, getTotalExpenses } = useExpenseStore();
  const { categories, getCategoryById } = useCategoryStore();

  const currentMonthExpenses = getCurrentMonthExpenses();
  const totalSpent = getTotalExpenses(currentMonthExpenses);

  // Category-wise spending data
  const categoryData = useMemo(() => {
    const categoryTotals = {};
    
    currentMonthExpenses.forEach(expense => {
      const category = getCategoryById(expense.category);
      const categoryName = category?.name || 'Unknown';
      const categoryColor = category?.color || '#9E9E9E';
      
      if (!categoryTotals[categoryName]) {
        categoryTotals[categoryName] = {
          name: categoryName,
          value: 0,
          color: categoryColor,
          icon: category?.icon || '❓'
        };
      }
      categoryTotals[categoryName].value += expense.amount;
    });

    return Object.values(categoryTotals).sort((a, b) => b.value - a.value);
  }, [currentMonthExpenses, getCategoryById]);

  // Priority-wise spending data
  const priorityData = useMemo(() => {
    const priorityTotals = {
      need: { name: 'Need', value: 0, color: PRIORITY_COLORS.need },
      want: { name: 'Want', value: 0, color: PRIORITY_COLORS.want },
      savings: { name: 'Savings', value: 0, color: PRIORITY_COLORS.savings }
    };

    currentMonthExpenses.forEach(expense => {
      if (priorityTotals[expense.priority]) {
        priorityTotals[expense.priority].value += expense.amount;
      }
    });

    return Object.values(priorityTotals).filter(item => item.value > 0);
  }, [currentMonthExpenses]);

  // Daily spending trend (last 7 days)
  const dailySpendingData = useMemo(() => {
    const last7Days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      const dayExpenses = currentMonthExpenses.filter(expense => {
        const expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
        return expenseDate.toDateString() === date.toDateString();
      });
      
      const dayTotal = dayExpenses.reduce((sum, expense) => sum + expense.amount, 0);
      
      last7Days.push({
        date: date.toLocaleDateString('en-BD', { weekday: 'short' }),
        amount: dayTotal,
        fullDate: date.toLocaleDateString('en-BD')
      });
    }
    
    return last7Days;
  }, [currentMonthExpenses]);

  // Calculate insights
  const insights = useMemo(() => {
    if (currentMonthExpenses.length === 0) return null;

    const avgTransaction = totalSpent / currentMonthExpenses.length;
    const highestCategory = categoryData[0];
    const mostFrequentPriority = priorityData.reduce((prev, current) => 
      prev.value > current.value ? prev : current
    );

    return {
      avgTransaction,
      highestCategory,
      mostFrequentPriority,
      totalTransactions: currentMonthExpenses.length
    };
  }, [currentMonthExpenses, totalSpent, categoryData, priorityData]);

  if (currentMonthExpenses.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Insights and trends for your family's spending</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <TrendingUp className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Yet</h3>
              <p className="text-gray-600 mb-4">
                Start adding expenses to see your spending analytics and insights.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-600">Insights and trends for your family's spending</p>
        </div>
        <Link to={ROUTES.ANALYTICS_TAGS}>
          <div className="flex items-center space-x-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer">
            <Tag className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Tags</span>
            <ChevronRight className="h-4 w-4 text-blue-600" />
          </div>
        </Link>
      </div>

      {/* Key Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalSpent)}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Transaction</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(insights?.avgTransaction || 0)}
            </div>
            <p className="text-xs text-muted-foreground">Per expense</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Category</CardTitle>
            <div className="text-lg">{insights?.highestCategory?.icon}</div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(insights?.highestCategory?.value || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {insights?.highestCategory?.name}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Transactions</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{insights?.totalTransactions}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Spending Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Spending by Category</CardTitle>
            <CardDescription>
              Distribution of expenses across categories
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {categoryData.slice(0, 5).map((category, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <span>{category.icon} {category.name}</span>
                  </div>
                  <span className="font-medium">{formatCurrency(category.value)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Priority Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Spending by Priority</CardTitle>
            <CardDescription>
              Need vs Want vs Savings distribution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Amount']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 space-y-2">
              {priorityData.map((priority, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: priority.color }}
                    />
                    <span>{priority.name}</span>
                  </div>
                  <span className="font-medium">{formatCurrency(priority.value)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Spending Trend */}
      <Card>
        <CardHeader>
          <CardTitle>Daily Spending Trend</CardTitle>
          <CardDescription>
            Your spending pattern over the last 7 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailySpendingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis tickFormatter={(value) => `৳${value}`} />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), 'Spent']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0]) {
                      return payload[0].payload.fullDate;
                    }
                    return label;
                  }}
                />
                <Bar dataKey="amount" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Spending Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Spending Insights</CardTitle>
          <CardDescription>
            Key observations about your spending patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
              <TrendingUp className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Top Spending Category</p>
                <p className="text-sm text-blue-700">
                  You spent the most on <strong>{insights?.highestCategory?.name}</strong> with {formatCurrency(insights?.highestCategory?.value || 0)} this month.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-900">Average Transaction</p>
                <p className="text-sm text-green-700">
                  Your average expense is {formatCurrency(insights?.avgTransaction || 0)} across {insights?.totalTransactions} transactions.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-purple-50 rounded-lg">
              <Calendar className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <p className="font-medium text-purple-900">Priority Focus</p>
                <p className="text-sm text-purple-700">
                  Most of your spending is on <strong>{insights?.mostFrequentPriority?.name}</strong> items with {formatCurrency(insights?.mostFrequentPriority?.value || 0)}.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
